import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { HeaderMenu } from '../components/HeaderMenu';
import { useAuth } from '../auth/context';
import { useOrg } from '../org/context';
import { useProject } from '../project/context';
import { roleLabel } from '../org/roleLabels';
import menu from '../components/HeaderMenu.module.css';
import styles from './AuthedBrand.module.css';

const PROJECT_PATH = /^\/projects\/([^/]+)/;

/** Decorative settings (gear) glyph — the accessible name comes from the menu trigger. */
function GearIcon() {
  return (
    <svg
      className={menu.icon}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

/** Decorative account (person) glyph. */
function AccountIcon() {
  return (
    <svg
      className={menu.icon}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

/**
 * Authenticated app header: the logo links home, a gear opens the settings menu,
 * and an account menu shows the active org/role and holds Sign out. Rendered as
 * the `brand` of {@link AppShell} on every authenticated screen.
 */
export function AuthedBrand() {
  const { logout } = useAuth();
  const { currentOrg } = useOrg();
  const { currentProject } = useProject();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [busy, setBusy] = useState(false);

  const segment = PROJECT_PATH.exec(pathname)?.[1];
  const projectId = segment && segment !== 'new' ? segment : null;

  const onLogout = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await logout();
    } finally {
      setBusy(false);
      navigate('/sign-in', { replace: true });
    }
  };

  const orgName = currentOrg?.name ?? 'Your organization';
  const roleText = currentOrg?.role ? roleLabel(currentOrg.role) : 'Member';

  return (
    <div className={styles.banner}>
      <Link to="/" className={styles.home} aria-label="Go to home">
        <img className={styles.logo} src="/brand/thinktankqa-logo.png" alt="Think Tank QA" />
      </Link>

      <nav className={styles.nav} aria-label="Main navigation">
        {projectId ? (
          <HeaderMenu
            label="Project pages"
            align="start"
            trigger={
              <>
                <span className={styles.navLabel} data-testid="current-project-name">
                  {currentProject?.name ?? 'Pages'}
                </span>
                <span className={styles.caret} aria-hidden="true">
                  ▾
                </span>
              </>
            }
          >
            {(close) => (
              <>
                <Link to={`/projects/${projectId}/scans`} className={menu.item} onClick={close}>
                  Scans
                </Link>
                <Link
                  to={`/projects/${projectId}/journey-health`}
                  className={menu.item}
                  onClick={close}
                >
                  Journey health
                </Link>
                <Link to={`/projects/${projectId}/journeys`} className={menu.item} onClick={close}>
                  Journeys
                </Link>
                <Link to={`/projects/${projectId}/defects`} className={menu.item} onClick={close}>
                  Defects
                </Link>
                <Link to={`/projects/${projectId}/test-cases`} className={menu.item} onClick={close}>
                  Test cases
                </Link>
                <Link
                  to={`/projects/${projectId}/tracker-settings`}
                  className={menu.item}
                  onClick={close}
                >
                  Tracker settings
                </Link>
                <Link
                  to={`/projects/${projectId}/connected-validation`}
                  className={menu.item}
                  onClick={close}
                >
                  Connected validation
                </Link>
                <Link to="/" className={menu.item} onClick={close}>
                  All projects
                </Link>
              </>
            )}
          </HeaderMenu>
        ) : null}

        <HeaderMenu label="Settings" trigger={<GearIcon />}>
          {(close) => (
            <>
              <Link to="/settings/members" className={menu.item} onClick={close}>
                Members
              </Link>
              <Link to="/settings/retention" className={menu.item} onClick={close}>
                Retention settings
              </Link>
              <Link to="/settings/llm-key" className={menu.item} onClick={close}>
                LLM key (BYOK)
              </Link>
              <Link to="/settings/integrations" className={menu.item} onClick={close}>
                Integrations
              </Link>
            </>
          )}
        </HeaderMenu>

        <HeaderMenu
          label="Account"
          trigger={
            <>
              <AccountIcon />
              <span className={styles.caret} aria-hidden="true">
                ▾
              </span>
            </>
          }
        >
          {(close) => (
            <>
              <div className={menu.panelHeader}>
                <span className={menu.panelHeaderName}>{orgName}</span>
                <span className={menu.panelHeaderMeta}>{roleText}</span>
              </div>
              <button
                type="button"
                className={menu.item}
                onClick={() => {
                  close();
                  void onLogout();
                }}
                disabled={busy}
              >
                {busy ? 'Signing out…' : 'Sign out'}
              </button>
            </>
          )}
        </HeaderMenu>
      </nav>
    </div>
  );
}
